/**
 * Admin API functions for the GDB Cloud Edition.
 *
 * Calls the existing Rust/Axum admin endpoints at /api/admin/* on the CLOUD.
 * Admin operates on cloud users/entitlements, so these go through authClient
 * (→ the cloud), NOT api (which resolves to the local sidecar in the editor).
 * Bearer token is auto-attached; all endpoints require admin role (server
 * enforces via the AdminUser extractor).
 */

import { authClient } from '@/lib/api-client'
import type {
  AdminActionResponse,
  AdminAnalytics,
  AdminNotesResponse,
  AdminUserDetail,
  AdminUserListResponse,
  ListUsersParams,
} from './types'

/**
 * List users with pagination and optional search.
 * GET /api/admin/users?page=N&limit=N&search=term
 */
export async function listUsers(
  params?: ListUsersParams
): Promise<AdminUserListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.page !== undefined) {
    searchParams.set('page', String(params.page))
  }
  if (params?.limit !== undefined) {
    searchParams.set('limit', String(params.limit))
  }
  if (params?.search) {
    searchParams.set('search', params.search)
  }

  const query = searchParams.toString()
  const path = query ? `/api/admin/users?${query}` : '/api/admin/users'

  const response = await authClient.get(path)
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to list users')
  }
  return response.json() as Promise<AdminUserListResponse>
}

/**
 * Get detailed information for a single user.
 * GET /api/admin/users/:id
 */
export async function getUserDetail(
  userId: string
): Promise<AdminUserDetail> {
  const response = await authClient.get(`/api/admin/users/${userId}`)
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to load user detail')
  }
  return response.json() as Promise<AdminUserDetail>
}

/**
 * Grant a module to a user.
 * POST /api/admin/users/:id/grant-module <- { moduleId: string }
 */
export async function grantModule(
  userId: string,
  moduleId: string
): Promise<AdminActionResponse> {
  const response = await authClient.post(`/api/admin/users/${userId}/grant-module`, {
    moduleId,
  })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to grant module')
  }
  return response.json() as Promise<AdminActionResponse>
}

/**
 * Revoke a module from a user.
 * POST /api/admin/users/:id/revoke-module <- { moduleId: string }
 */
export async function revokeModule(
  userId: string,
  moduleId: string
): Promise<AdminActionResponse> {
  const response = await authClient.post(`/api/admin/users/${userId}/revoke-module`, {
    moduleId,
  })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to revoke module')
  }
  return response.json() as Promise<AdminActionResponse>
}

/**
 * Disable a user account.
 * POST /api/admin/users/:id/disable
 */
export async function disableUser(
  userId: string
): Promise<AdminActionResponse> {
  const response = await authClient.post(`/api/admin/users/${userId}/disable`)
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to disable user')
  }
  return response.json() as Promise<AdminActionResponse>
}

/**
 * Enable a disabled user account.
 * POST /api/admin/users/:id/enable
 */
export async function enableUser(
  userId: string
): Promise<AdminActionResponse> {
  const response = await authClient.post(`/api/admin/users/${userId}/enable`)
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to enable user')
  }
  return response.json() as Promise<AdminActionResponse>
}

/**
 * Change a user's role.
 * POST /api/admin/users/:id/change-role <- { role: string }
 */
export async function changeRole(
  userId: string,
  role: string
): Promise<AdminActionResponse> {
  const response = await authClient.post(`/api/admin/users/${userId}/change-role`, {
    role,
  })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to change role')
  }
  return response.json() as Promise<AdminActionResponse>
}

/**
 * List admin notes for a user.
 * GET /api/admin/users/:id/notes
 */
export async function listNotes(
  userId: string
): Promise<AdminNotesResponse> {
  const response = await authClient.get(`/api/admin/users/${userId}/notes`)
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to load notes')
  }
  return response.json() as Promise<AdminNotesResponse>
}

/**
 * Create an admin note on a user.
 * POST /api/admin/users/:id/notes <- { note: string }
 */
export async function createNote(
  userId: string,
  note: string
): Promise<AdminActionResponse> {
  const response = await authClient.post(`/api/admin/users/${userId}/notes`, { note })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to create note')
  }
  return response.json() as Promise<AdminActionResponse>
}

/**
 * Soft-delete (anonymize) a user. Super-admin only.
 * DELETE /api/admin/users/:id
 *
 * On the /api/admin user-management surface alongside disable/enable and
 * grant-module (every admin call in this file goes to /api/admin/* on the
 * cloud). The server anonymizes the user (scrubs PII, disables login) and
 * removes them from the admin user list.
 * Server enforces 403 on self-delete or deleting another super-admin and 404
 * when the user does not exist; this client just surfaces the error message.
 */
export async function deleteUser(
  userId: string
): Promise<AdminActionResponse> {
  const response = await authClient.delete(`/api/admin/users/${userId}`)
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to delete user')
  }
  return response.json() as Promise<AdminActionResponse>
}

/**
 * Get platform analytics.
 * GET /api/admin/analytics
 */
export async function getAnalytics(): Promise<AdminAnalytics> {
  const response = await authClient.get('/api/admin/analytics')
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to load analytics')
  }
  return response.json() as Promise<AdminAnalytics>
}

// Phase 16 Plan 08: listProducts / getProduct / createProduct / updateProduct
// API functions deleted. The /api/admin/products endpoints they hit no longer
// exist on the server — /console/products consumes /api/cms/admin/products
// via @/core/console/controllers/queries/useProductsAdmin + ../mutations/
// useProductsMutations (Plan 16-03 + Plan 16-04).
